import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Visitor } from 'sb-lib/types';
import {
  RealtimeState,
  selectRealtimeIntendedVisitor,
  selectRealtimeAssociatedVisitor,
  selectServerDisconnected,
} from 'shared/store/realtime-slice';
import { selectActiveVisitor } from 'shared/store/shared-selectors';
import type { AppState } from 'shared/store/reducers';
import { makeLog } from 'shared/utils/log';
import {
  associateVisitor as managerAssociateVisitor,
  dissociateVisitor as managerDissociateVisitor,
  kickAgentFromVisitor as managerKickAgentFromVisitor,
} from './manager';
import { useAppSelector } from 'shared/store';
import { usePrevious } from 'shared/hooks';
import { VisitorAssociateErrorCode } from 'types/realtime';
import { ErrorWithCode, VisitorAssociationStatus } from 'types';
import {
  getIntendedAssocVisitor,
  getIntendedVisitor,
  createRealtimeReducerActions,
  createKit,
} from './kit';

const log = makeLog('association-kit');

/**
 *
 * Association flow
 *
 */

/****************************************************
 *
 *                 Thunks
 *
 ****************************************************/
type AssociationWantedPayload = { visitorId: string | null };
export const associationWanted = createAsyncThunk<
  AssociationWantedPayload,
  AssociationWantedPayload,
  { state: AppState }
>('realtime/associationWanted', async ({ visitorId }) => {
  return { visitorId };
});

export const associateActiveVisitor = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/associateActiveVisitor',
  async (_, { getState }) => {
    const activeVisitor = selectActiveVisitor(getState());
    if (activeVisitor) {
      await managerAssociateVisitor(activeVisitor);
    } else {
      log.debug(`Cannot associate without active visitor`);
    }
  },
);

export const dissociateVisitor = createAsyncThunk<
  { sbid: string },
  { sbid: string; message?: string; isKick?: boolean },
  { state: AppState }
>('realtime/dissociateVisitor', async ({ sbid }, { getState, dispatch }) => {
  const { isConnectedToServer } = getState().realtime;
  if (isConnectedToServer) {
    // send dissoc request to server
    await managerDissociateVisitor(sbid);
  } else {
    // straight update to state
    dispatch(actions.offlineDissociatedVisitor());
  }
  return { sbid };
});

export const kickAgentFromVisitor = createAsyncThunk<
  void,
  { agentId: Visitor['helpedBy']; visitorId: Visitor['sbid'] }
>('realtime/kickAgentFromVisitor', async ({ agentId, visitorId }) => {
  managerKickAgentFromVisitor(agentId, visitorId);
});

/****************************************************
 *
 *                 Reducers
 *
 ****************************************************/
const reducers = {
  setAssociationStatus: (
    state: RealtimeState,
    action: PayloadAction<{ status: VisitorAssociationStatus; message?: string }>,
  ) => {
    state.associationStatus = action.payload.status;
    state.associationStatusMsg = action.payload.message || null;
  },
  offlineDissociatedVisitor: (state: RealtimeState) => {
    state.associationStatus = 'off';
    state.associationStatusMsg = null;
  },
  pauseVisitorAssociation: (state: RealtimeState, action: PayloadAction<boolean>) => {
    state.associationStatus = action.payload ? 'paused' : 'off';
  },
};
// Access actions before createSlice so we can reference them locally
const actions = createRealtimeReducerActions({ reducers });

export const associationKit = createKit({
  reducers,
  extraReducers: builder => {
    // associationWanted
    builder.addCase(associationWanted.pending, (state, action) => {
      const { visitorId } = action.meta.arg;
      if (visitorId && state.intendedVisitorId !== visitorId) {
        state.associationStatus = 'wanted';
        state.associationStatusMsg = null;
      }

      if (!visitorId) {
        state.associationStatus = 'off';
        state.associationStatusMsg = null;
      }

      state.intendedVisitorId = visitorId;
    });

    builder.addCase(associationWanted.rejected, (state, action) => {
      state.associationStatus = 'failed';
      state.associationStatusMsg = `Failed to associated ${action.error?.message}`;
    });

    // associateActiveVisitor
    builder.addCase(associateActiveVisitor.pending, state => {
      state.associationStatus = 'establishing';
    });
    builder.addCase(associateActiveVisitor.rejected, (state, action) => {
      // associationStatus is set to 'active' when the sbid_data comes back with our agent sid (helpedBySid)
      if (action.error) {
        const err = action.error as ErrorWithCode;
        if (err.code === VisitorAssociateErrorCode.ALREADY_ASSOCIATED) {
          state.associationStatus = 'busy';
          state.associationStatusMsg = null;
        } else {
          state.associationStatus = 'failed';
          state.associationStatusMsg = `Unknown failure: ${err.message}`;
        }
      }
    });

    // dissociateVisitor
    builder.addCase(dissociateVisitor.pending, (state, action) => {
      state.dissociatingFromSbid = action.meta.arg.sbid;
      if (action.meta.arg?.isKick) {
        log.debug('Kicked off visitor');
        state.associationStatus = 'kicked';
      }
    });
    builder.addCase(dissociateVisitor.fulfilled, (state, action) => {
      state.dissociatingFromSbid = null;
      if (action.meta.arg?.isKick) {
        log.debug('Kicked off visitor');
        state.associationStatus = 'kicked';
      }
      if (action.meta.arg?.message) {
        state.associationStatusMsg = action.meta.arg.message;
      }
    });
    builder.addCase(dissociateVisitor.rejected, state => {
      state.dissociatingFromSbid = null;
    });
  },

  /****************************************************
   *
   *               Handle triggers
   *
   ****************************************************/
  triggerThunkHelper: () => null,

  /****************************************************
   *
   *         Visitor sync/update reducer helpers
   *
   ****************************************************/
  visitorUpdateReducer: (state, userId) => {
    const activeVis = getIntendedVisitor(state);
    const assocVis = getIntendedAssocVisitor(state);
    const isKicked = state.associationStatus === 'kicked';
    if (assocVis) {
      state.associationStatus = 'active';
      state.associationStatusMsg = null;
    } else if (userId && activeVis?.helpedBy === userId) {
      state.associationStatus = 'wanted';
      state.associationStatusMsg = null;
    } else if (!activeVis?.helpedBy) {
      if (!['wanted', 'establishing', 'paused'].includes(state.associationStatus)) {
        state.associationStatus = isKicked ? 'kicked' : 'off';
        state.associationStatusMsg = null;
      }
    } else {
      state.associationStatus = isKicked ? 'kicked' : 'busy';
      state.associationStatusMsg = null;
    }
  },

  /****************************************************
   *
   *               Kit hook
   *
   ****************************************************/
  useKitHook: () => {
    const dispatch = useDispatch();
    const {
      associationStatus,
      isConnectedToServer,
      intendedVisitorId,
      visitorMap,
      sid,
    } = useAppSelector(s => s.realtime);
    const previousIntendedVisitorId = usePrevious(intendedVisitorId);
    // get the current realtime visitor so we can see if this agent/sid is associated right now
    const previousIntendedVisitorCurrentState = previousIntendedVisitorId
      ? visitorMap[previousIntendedVisitorId]
      : null;
    const intendedVisitorSid = intendedVisitorId && visitorMap[intendedVisitorId]?.sid;
    const associatedVisitor = useAppSelector(selectRealtimeAssociatedVisitor);
    const isIntendedVisitorOnline = !!useAppSelector(selectRealtimeIntendedVisitor);
    const intentionalDisconnect = useAppSelector(selectServerDisconnected);

    useEffect(
      function dissociateWhenSocketDisconnected() {
        const associated = associationStatus === 'active';
        if (intentionalDisconnect && associated && intendedVisitorId) {
          log.debug(
            `Intentional socket disconnect and associated, will try to dissociate`,
          );
          dispatch(dissociateVisitor({ sbid: intendedVisitorId }));
        }
      },
      [associationStatus, dispatch, intentionalDisconnect, intendedVisitorId],
    );

    useEffect(
      function dissociateWhenIntendedVisitorChanges() {
        if (
          previousIntendedVisitorId &&
          previousIntendedVisitorId !== intendedVisitorId &&
          previousIntendedVisitorCurrentState?.helpedBySid === sid
        ) {
          if (intendedVisitorSid !== previousIntendedVisitorCurrentState?.sid) {
            log.debug(
              `Intended visitorId changed and we are associated, dissociate (sbid=${previousIntendedVisitorId}, newSbid=${intendedVisitorId})`,
            );
            dispatch(dissociateVisitor({ sbid: previousIntendedVisitorId }));
          } else {
            log.debug(
              `Associated and intended visitorId changed from ${previousIntendedVisitorId} to ${intendedVisitorId} but sid is the same (sid=${intendedVisitorSid}), will NOT dissociate`,
            );
          }
        }
      },
      [
        dispatch,
        previousIntendedVisitorId,
        intendedVisitorId,
        intendedVisitorSid,
        previousIntendedVisitorCurrentState,
        sid,
      ],
    );

    useEffect(
      function associateWhenSocketConnected() {
        if (
          isConnectedToServer &&
          intendedVisitorId &&
          isIntendedVisitorOnline &&
          !['active', 'establishing', 'busy', 'kicked', 'paused', 'failed'].includes(
            associationStatus,
          )
        ) {
          log.debug(
            `Socket connected and we want to be associated, will try to associate (sbid=${intendedVisitorId})`,
          );
          dispatch(associateActiveVisitor());
        }
      },
      [
        intendedVisitorId,
        associationStatus,
        dispatch,
        isConnectedToServer,
        isIntendedVisitorOnline,
      ],
    );

    useEffect(
      function dissociateWhenPaused() {
        if (associationStatus === 'paused' && associatedVisitor) {
          log.debug('Association has been paused, will dissociate');
          dispatch(dissociateVisitor({ sbid: associatedVisitor.sbid }));
        }
      },
      [associatedVisitor, associationStatus, dispatch],
    );
  },
});
